/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-26
 */

// Add third-party dependencies.
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  Divider,
  List,
  ListItem,
  Avatar,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  Business as StudioIcon,
  Build as ToolsIcon,
  AttachMoney as PricingIcon,
  Support as SupportIcon,
  Person as PersonIcon,
  Login as LoginIcon,
  PersonAdd as SignUpIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Add local dependencies.
import NavigationItem from '../common/NavigationItem';
import { useSessionAuth } from '../../../hooks/useSessionAuth';
import { useSignOut } from '../../../hooks/useSignOut';
import type { RootState } from '../../../store';

/**
 * Props for the MobileMenu component.
 */
interface MobileMenuProps {
  /**
   * Whether the mobile menu is open.
   */
  isOpen: boolean;
  /**
   * Callback when the menu should be closed.
   */
  onClose: () => void;
}

/**
 * Navigation item data structure for mobile.
 */
interface MobileNavigationItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  requiresAuth?: boolean;
}

/**
 * Mobile navigation menu component that provides a slide-out drawer
 * with all navigation items optimized for touch interfaces.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The mobile menu component
 *
 * @example
 * return (
 *   <MobileMenu
 *     isOpen={isMobileMenuOpen}
 *     onClose={() => setIsMobileMenuOpen(false)}
 *   />
 * );
 */
const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated } = useSessionAuth();
  const user = useSelector((state: RootState) => state.auth.user);
  const { signOut, isSigningOut, signOutError } = useSignOut();

  // Define mobile navigation items
  const navigationItems: MobileNavigationItem[] = [
    // Authenticated user items
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: <DashboardIcon />,
      requiresAuth: true,
    },
    {
      label: 'Studios',
      path: '/studios',
      icon: <StudioIcon />,
      requiresAuth: true,
    },
    {
      label: 'Tools',
      path: '/tools',
      icon: <ToolsIcon />,
      requiresAuth: true,
    },
    // Public items
    {
      label: 'Pricing',
      path: '/pricing',
      icon: <PricingIcon />,
      requiresAuth: false,
    },
    {
      label: 'Support',
      path: '/contact',
      icon: <SupportIcon />,
      requiresAuth: false,
    },
  ];

  // Add authentication items for non-authenticated users
  const authItems: MobileNavigationItem[] = [
    {
      label: 'Sign In',
      path: '/login',
      icon: <LoginIcon />,
      requiresAuth: false,
    },
    {
      label: 'Sign Up',
      path: '/register',
      icon: <SignUpIcon />,
      requiresAuth: false,
    },
  ];

  // Filter items based on authentication status and current page
  const visibleItems = navigationItems.filter(item => {
    if (item.requiresAuth && !isAuthenticated) {
      return false;
    }
    // Hide navigation item when currently on that page
    if (item.path === window.location.pathname) {
      return false;
    }
    return true;
  });

  // Add auth items for non-authenticated users
  const allVisibleItems = isAuthenticated
    ? visibleItems
    : [
        ...visibleItems,
        ...authItems.filter(item => item.path !== window.location.pathname),
      ];

  /**
   * Handle navigation item click.
   */
  const handleNavigationClick = (path: string) => {
    navigate(path);
    onClose();
  };

  /**
   * Handle profile click.
   */
  const handleProfileClick = () => {
    navigate('/profile');
    onClose();
  };

  return (
    <Drawer
      anchor="left"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 280,
          backgroundColor: theme.palette.background.paper,
        },
      }}
      // Improve performance on mobile
      ModalProps={{
        keepMounted: true,
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header with close button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 600,
            }}
          >
            tools.dance
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              minWidth: 44,
              minHeight: 44,
            }}
            aria-label="Close menu"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* User profile section (authenticated users only) */}
        {isAuthenticated && user && (
          <>
            <Box
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                cursor: 'pointer',
                minHeight: 64, // Touch-friendly height
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
              onClick={handleProfileClick}
              role="button"
              tabIndex={0}
              aria-label="Go to profile"
            >
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: theme.palette.primary.main,
                }}
              >
                <PersonIcon />
              </Avatar>
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 500,
                    lineHeight: 1.2,
                  }}
                >
                  {user.first_name} {user.last_name}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    lineHeight: 1.2,
                  }}
                >
                  {user.email}
                </Typography>
              </Box>
            </Box>
            <Divider />
          </>
        )}

        {/* Navigation items */}
        <List
          sx={{
            py: 1,
          }}
        >
          {allVisibleItems.map(item => (
            <ListItem
              key={item.path}
              sx={{
                px: 0,
                py: 0.5,
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  px: 2,
                }}
              >
                <NavigationItem
                  label={item.label}
                  path={item.path}
                  icon={item.icon}
                  onClick={() => handleNavigationClick(item.path)}
                />
              </Box>
            </ListItem>
          ))}
        </List>

        {/* Sign out (the header's user menu is hidden on small screens) */}
        {isAuthenticated && user && (
          <>
            <Divider />
            <Box sx={{ px: 2, py: 1.5 }}>
              <NavigationItem
                label={isSigningOut ? 'Signing out...' : 'Sign Out'}
                path=""
                icon={<LogoutIcon />}
                isActive={false}
                disabled={isSigningOut}
                onClick={signOut}
              />
              {signOutError && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {signOutError}
                </Typography>
              )}
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default MobileMenu;
